import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "lib/utils";

const badgeVariants = cva(
    "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 justify-center",
    {
        variants: {
            variant: {
                default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
                secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
                destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
                outline: "text-foreground",
                featured:
                    "gap-1 rounded-full border-transparent bg-primary px-3 py-1 text-xs uppercase tracking-wide text-primary-foreground [&_svg]:h-3.5 [&_svg]:w-3.5",
                check: "gap-2 rounded-2xl border-border/40 px-4 py-2 text-sm shadow-sm bg-white hover:border-border",
                advantage:
                    "h-auto w-full items-start justify-start gap-2 whitespace-normal rounded-xl border-none bg-muted/40 px-3 py-2.5 text-left text-sm font-normal normal-case tracking-normal text-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-emerald-500",
                category: "gap-2 rounded-full border-transparent px-3 py-1 uppercase tracking-wide",
                categorySquare:
                    "gap-2 rounded-lg border-transparent px-3 py-1.5 text-sm font-bold normal-case tracking-normal focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 [&>div]:inline-flex [&>div]:items-center [&>div]:gap-2 [&_svg]:h-3.5 [&_svg]:w-3.5",
                ghost: "gap-2 rounded-full border-white/20 bg-white/10 px-3 py-1 uppercase tracking-wide text-current [&_svg]:h-3.5 [&_svg]:w-3.5",
                frequency:
                    "gap-1 rounded-full border-transparent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide [&_svg]:h-3 [&_svg]:w-3",
                available:
                    "gap-1 rounded-full border-transparent bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 [&_svg]:h-2.5 [&_svg]:w-2.5",
                unavailable:
                    "gap-1 rounded-full border-transparent bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 [&_svg]:h-2.5 [&_svg]:w-2.5",
                pro: "rounded-lg border-transparent bg-gradient-to-r from-orange-400 to-orange-600 px-2 py-1 text-xs font-bold uppercase tracking-widest text-white",
                iconTile:
                    "gap-0 rounded-xl border-transparent p-0 font-normal normal-case tracking-normal [&_svg]:pointer-events-none",
            },
            size: {
                tileSm: "h-8 w-8 min-w-8 [&_svg]:size-4",
                tileMd: "h-10 w-10 min-w-10 [&_svg]:size-5",
            },
            palette: {
                indigo: "border-transparent bg-indigo-600 px-3 py-1.5 uppercase tracking-wide text-white hover:bg-indigo-600",
                emerald: "border-transparent bg-emerald-600 px-3 py-1.5 uppercase tracking-wide text-white hover:bg-emerald-600",
                purple: "border-transparent bg-purple-600 px-3 py-1.5 uppercase tracking-wide text-white hover:bg-purple-600",
                violet: "border-transparent bg-violet-100 text-violet-700",
                orange: "border-transparent bg-orange-100 text-orange-700",
                pink: "border-transparent bg-pink-100 text-pink-700",
                blue: "border-transparent bg-blue-100 text-blue-700",
                amber: "border-transparent bg-amber-100 text-amber-700",
                green: "border-transparent bg-green-100 text-green-700",
                teal: "border-transparent bg-teal-100 text-teal-700",
            },
            icon: {
                dot: "before:h-2 before:w-2 before:rounded-full",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, palette, size, icon, children, ...props }: BadgeProps) {
    const content = React.Children.toArray(children);

    return (
        <div className={cn(badgeVariants({ variant, palette, size, icon }), className)} {...props}>
            {content.map((child, index) => (
                <div key={index} className="flex items-center justify-center">
                    {child}
                </div>
            ))}
        </div>
    );
}

export { Badge, badgeVariants };
